<template>
  <div>
    <v-dialog v-model="dialog" width="800" persistent>
      <v-card v-if="loading" flat>
        <v-row justify-center class="pa-5">
          <v-progress-circular indeterminate color="red" size="64" />
        </v-row>
      </v-card>
      <v-card v-if="!loading" flat>
        <v-toolbar flat color="red-darken-3">
          <v-toolbar-title>
            {{ $t("part_requests.offers.label") }}
          </v-toolbar-title>
        </v-toolbar>

        <!----------------------------------------MAIN OFFER----------------------------------------->
        <v-row
          v-if="assigned && status && status !== 4"
          row
          wrap
          class="pt-2 pl-4 pr-4"
        >
          <!--PRICE-->
          <v-col xs="4" class="pr-1">
            <v-text-field
              v-model="offer.price"
              :disabled="!editable"
              :label="$t('part_requests.offers.price')"
              color="red"
              type="number"
              min="0"
              oninput="validity.valid||(value='');"
            />
          </v-col>
          <!--FEE-->
          <v-col xs="4" class="pl-1 pr-1">
            <v-text-field
              v-model="offer.fee"
              :disabled="!editable"
              :label="$t('part_requests.offers.fee')"
              color="red"
              type="number"
              min="0"
              oninput="validity.valid||(value='');"
            />
          </v-col>
          <!--CURRENCY-->
          <v-col xs="4" class="pl-1">
            <v-select
              v-model="offer.currency"
              :disabled="!editable"
              :items="currencies"
              :label="$t('part_requests.offers.currency')"
              color="red"
            />
          </v-col>
          <!--DESCRIPTION-->
          <v-col xs="12">
            <v-textarea
              v-model="offer.description"
              :disabled="!editable"
              :label="$t('part_requests.offers.description')"
              color="red"
            />
          </v-col>
          <!--IMAGE GRID-->
          <v-col xs="12">
            <v-container grid-list-sm fluid>
              <v-row row wrap>
                <!--EXISTING IMAGES-->
                <v-col
                  v-for="(image, index) in offer.images"
                  :key="image"
                  xs="6"
                  sm="4"
                  d-flex
                >
                  <v-hover>
                    <template #default="{ hover }">
                      <v-card flat title class="d-flex">
                        <v-img
                          :src="`${server}/images/part-request/?q=${
                            $store.getters.institutionId
                          }-${image}`"
                        >
                          <v-row row ma-1>
                            <v-spacer />
                            <v-icon
                              v-if="hover"
                              x-large
                              color="red"
                              class="pointer"
                              @click="deleteImage(true, index, image)"
                            >
                              delete_forever
                            </v-icon>
                          </v-row>
                        </v-img>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col>
                <!--NEW IMAGES-->
                <v-col
                  v-for="(image, index) in images"
                  :key="index"
                  xs="6"
                  sm="4"
                  d-flex
                >
                  <v-hover>
                    <template #default="{ hover }">
                      <v-card flat title class="d-flex">
                        <v-img :src="image.dataUrl">
                          <v-row row ma-1>
                            <v-spacer />
                            <v-icon
                              v-if="hover"
                              x-large
                              color="red"
                              class="pointer"
                              @click="deleteImage(false, index, image)"
                            >
                              mdi-delete
                            </v-icon>
                          </v-row>
                        </v-img>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
          <!--IMAGES-->
          <v-col xs="12">
            <v-row justify-center>
              <v-btn
                v-if="!showCamera"
                :disabled="!editable"
                variant="outlined"
                color="primary"
                @click="showCamera = true"
              >
                <v-icon left> mdi-camera </v-icon>
                {{ $t("part_requests.offers.take_pictures") }}
              </v-btn>
            </v-row>
            <!--CAMERA COMPONENT-->
            <camera v-if="showCamera" @take-picture="takePicture" />
          </v-col>
        </v-row>

        <!------------------------------------------------------------------------------------------->
        <v-divider
          v-if="
            ($store.getters.role === 'LekoAdmin' ||
              $store.getters.role === 'LekoEmployee' ||
              $store.getters.role === 'SuperAdmin') &&
            otherInstitutions.length > 0
          "
          class="mt-4 mb-2"
        />
        <!------------------------------------------------------------------------------------------->

        <!---------------------------------------OTHER OFFERS---------------------------------------->
        <v-row
          v-if="
            ($store.getters.role === 'LekoAdmin' ||
              $store.getters.role === 'LekoEmployee' ||
              $store.getters.role === 'SuperAdmin') &&
            otherInstitutions.length > 0
          "
          justify-center
          class="mb-1"
        >
          <span class="title">{{
            $t("part_requests.offers.other_offers")
          }}</span>
        </v-row>
        <v-row
          v-if="
            ($store.getters.role === 'LekoAdmin' ||
              $store.getters.role === 'LekoEmployee' ||
              $store.getters.role === 'SuperAdmin') &&
            otherInstitutions.length > 0
          "
          row
          wrap
          class="pl-2 pr-2"
        >
          <v-col
            v-for="(institution, index) in otherInstitutions"
            :key="index"
            xs="6"
            sm="4"
            ma-1
          >
            <v-card
              flat
              class="mx-auto"
              :color="getOfferStatus(institution).color"
              :dark="getOfferStatus(institution).dark"
            >
              <v-card-title>
                <v-row align-center justify-center>
                  <span class="title">
                    {{ getOfferStatus(institution).status }}
                  </span>
                </v-row>
              </v-card-title>

              <v-row column class="ml-2">
                <v-col>
                  <div class="caption">
                    {{ $t("part_requests.offers.institution") }}:
                  </div>
                  <div class="subheading font-weight-medium">
                    {{ institution.id.title }}
                  </div>
                  <div class="caption">
                    {{ $t("part_requests.offers.price") }}:
                  </div>
                  <div class="font-weight-bold">
                    {{ institution.offer.price }}
                    {{ institution.offer.currency }}
                  </div>
                </v-col>
                <v-col>
                  <div class="caption">
                    {{ $t("part_requests.offers.description") }}:
                  </div>
                  <div class="font-weight-bold">
                    {{ institution.offer.description }}
                  </div>
                </v-col>
                <v-col>
                  <v-row row wrap>
                    <v-col
                      v-for="(image, i) in institution.offer.images"
                      :key="i"
                      xs="6"
                      sm="4"
                      d-flex
                      class="pa-1"
                    >
                      <v-img
                        :src="`${server}/images/part-request/?q=${
                          institution.id._id
                        }-${image}`"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-card-actions>
          <v-btn
            :disabled="!assigned || status === 4 || !hasOffers || !editable"
            variant="outlined"
            color="error"
            @click="deleteOffer"
          >
            {{ $t("part_requests.offers.delete_offer") }}
          </v-btn>
          <v-spacer />
          <v-btn variant="outlined" color="error" @click="close">
            {{ $t("part_requests.close") }}
          </v-btn>
          <v-btn
            :disabled="!assigned || status === 4 || !offer.price || !editable"
            variant="outlined"
            color="primary"
            :loading="loadingSave"
            @click="saveOffer"
          >
            {{ $t("part_requests.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert class="mb-0" :value="true" type="error">
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete()">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// import axios from 'axios';

import camera from "../camera.vue";
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import deleteRequest from "../../api/deleteRequest";

function generateRandomHexString(length) {
  const chars = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
  ];
  let randomstring = "";
  for (let i = 0; i < length; i += 1) {
    const random = Math.floor(Math.random() * Math.floor(chars.length));
    randomstring += chars[random];
  }
  const timestamp = new Date().getTime().toString(16);
  return `${randomstring}${timestamp}`;
}

export default {
  components: {
    camera,
  },
  data: () => ({
    dialog: false,
    editable: false,
    loading: false,
    loadingSave: false,
    loadingDelete: false,
    showCamera: false,
    requestId: null,
    currencies: ["EUR"], // 'ALL',
    hasOffers: false,
    offer: {
      price: null,
      fee: null,
      currency: "EUR",
      description: "",
      images: [],
    },
    otherInstitutions: [],
    images: [],
    imagesToDelete: [],
    assigned: false,
    status: null,
    dialogDelete: false,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
  },
  methods: {
    open(data) {
      this.requestId = data.id;
      this.editable = data.editable;

      this.assigned = !!data.assignedInstitutions.find(
        (i) =>
          i.id._id.toString() === this.$store.getters.institutionId.toString(),
      );

      this.dialog = true;
      this.loading = true;

      getRequest(
        "/part-request/offers",
        `request_id=${this.requestId}&institution_id=${this.$store.getters.institutionId}&role=${this.$store.getters.role}`,
      )
        .then((response) => {
          if (response.offer) this.offer = response.offer;
          if (response.otherInstitutions)
            this.otherInstitutions = response.otherInstitutions;
          this.hasOffers = response.found;
          this.status = response.status;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    close() {
      this.dialog = false;
      this.requestId = null;
      this.assigned = false;
      this.loading = false;
      this.loadingSave = false;
      this.loadingDelete = false;
      this.showCamera = false;
      this.images = [];
      this.imagesToDelete = [];
      this.offer = {
        price: null,
        fee: null,
        currency: "EUR",
        description: "",
        images: [],
      };
      this.otherInstitutions = [];
      this.hasOffers = false;
      this.status = null;
      this.$emit("close");
    },
    takePicture(image) {
      this.images.push({
        dataUrl: image.dataUrl,
        blob: image.blob,
        title: `offerimage-${generateRandomHexString(6)}.png`,
      });
    },
    deleteImage(existing, index, image) {
      if (existing) {
        this.imagesToDelete.push(image);
        this.offer.images.splice(index, 1);
      } else {
        this.images.splice(index, 1);
      }
    },
    saveOffer() {
      this.loadingSave = true;
      const formData = new FormData();

      // Check if there are any images to delete.
      const indexes = this.imagesToDelete.map((image) =>
        this.offer.images.findIndex((i) => i === image),
      );
      indexes.forEach((i) => {
        if (i > -1) this.offer.images.splice(i, 1);
      });

      const images = [];
      // Push existing images to the final array.
      this.offer.images.forEach((i) => images.push(i));
      // Push newly added images to the final array.
      this.images.forEach((i) => images.push(i.title));

      // Append data.
      formData.append(
        "data",
        JSON.stringify({
          imagesToDelete: this.imagesToDelete,
          institutionId: this.$store.getters.institutionId,
          offer: {
            proposed: true,
            price: this.offer.price,
            fee: this.offer.fee,
            currency: this.offer.currency,
            description: this.offer.description,
            images,
          },
        }),
      );

      // Append new image files.
      this.images.forEach((image) =>
        formData.append(
          "request-images",
          image.blob,
          `${this.$store.getters.institutionId}-${image.title}`,
        ),
      );

      postRequest
        .formData(
          "/part-request/offer",
          `request_id=${this.requestId}`,
          formData,
        )
        .then(() => {
          this.close();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingSave = false;
        });
    },
    deleteOffer() {
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loadingDelete = true;
      deleteRequest(
        "/part-request/offer",
        `request_id=${this.requestId}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          this.loadingDelete = false;
          this.dialogDelete = false;
          this.close();
        });
    },
    getOfferStatus(institution) {
      if (institution.offer.selected)
        return {
          dark: true,
          color: "green",
          status: this.$t("part_requests.offers.selected"),
        };
      if (institution.offer.refused)
        return {
          dark: true,
          color: "red",
          status: this.$t("part_requests.offers.refused"),
        };
      return { dark: false, color: "", status: "" };
    },
  },
};
</script>
